import { useState } from "react";
import { useLocation } from "wouter";
import {
  Bookmark,
  Sparkles,
  ListChecks,
  ArrowRight,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Play,
  Music2,
  Globe,
  FlaskConical,
  ShieldCheck,
  FileText,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuthStore } from "@/lib/stores/authStore";

const FEATURES = [
  { icon: Bookmark, title: "Captura desde cualquier lugar", desc: "Enlace, vídeo o hilo. Yo me encargo del resto." },
  { icon: Sparkles, title: "Te lo dejo listo", desc: "Resumen, temática, etiquetas y utilidad." },
  { icon: ListChecks, title: "Reencuentra y conviértelo en trabajo", desc: "Notas, tareas, ideas y proyectos." },
];

const FlowGraphic = () => (
  <div className="flex items-center gap-3" aria-hidden="true">
    <div className="rounded-xl border bg-card p-2.5 grid grid-cols-1 gap-2">
      <span className="w-7 h-7 rounded-md bg-red-600 text-white grid place-items-center">
        <Play className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
      </span>
      <span className="w-7 h-7 rounded-md bg-stone-900 text-white grid place-items-center">
        <Music2 className="h-3.5 w-3.5" strokeWidth={1.75} />
      </span>
      <span className="w-7 h-7 rounded-md bg-black text-white grid place-items-center font-black text-xs">X</span>
      <span className="w-7 h-7 rounded-md bg-sky-700 text-white grid place-items-center">
        <Globe className="h-3.5 w-3.5" strokeWidth={1.75} />
      </span>
    </div>
    <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
    <div className="w-16 h-16 rounded-2xl border bg-card grid place-items-center relative">
      <div className="w-9 h-9 rounded-lg bg-primary text-primary-foreground grid place-items-center font-serif text-lg">A</div>
      <Sparkles className="absolute -top-1.5 -right-1.5 h-4 w-4 text-primary" strokeWidth={1.75} />
    </div>
    <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
    <div className="rounded-xl border bg-card p-3 w-44 space-y-1.5">
      <div className="h-1.5 w-3/4 rounded bg-secondary" />
      <div className="h-1.5 w-1/2 rounded bg-secondary" />
      <div className="flex gap-1 pt-1">
        <span className="rounded bg-secondary px-1.5 py-0.5 text-[9px] font-mono text-muted-foreground">#IA</span>
        <span className="rounded bg-secondary px-1.5 py-0.5 text-[9px] font-mono text-muted-foreground">#dev</span>
      </div>
      {["Resumen", "Temática", "Etiquetas", "Utilidad"].map((t) => (
        <p key={t} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <Check className="h-2.5 w-2.5 text-green-700" strokeWidth={2.5} /> {t}
        </p>
      ))}
    </div>
  </div>
);

export default function Login() {
  const loginDemo = useAuthStore((s) => s.loginDemo);
  const [, navigate] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);

  const enter = (e) => {
    e?.preventDefault();
    loginDemo();
    navigate("/inbox");
  };

  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-2" data-testid="login-page">
      {/* Panel de marca (escritorio) */}
      <div className="hidden lg:flex flex-col justify-between p-10 xl:p-14">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-primary text-primary-foreground grid place-items-center font-serif text-xl">
            A
          </div>
          <span className="font-serif text-2xl tracking-tight">Aviando</span>
        </div>

        <div className="space-y-8 max-w-lg">
          <div>
            <h1 className="font-serif text-5xl xl:text-6xl leading-[1.05] tracking-tight">
              Compártelo
              <br />y olvídate.
            </h1>
            <p className="mt-4 text-muted-foreground text-[15px] leading-relaxed">
              Guardo, entiendo y organizo el contenido que encuentras para que puedas reencontrarlo y convertirlo en trabajo.
            </p>
          </div>

          <div className="space-y-4">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent grid place-items-center shrink-0">
                  <Icon className="h-[18px] w-[18px] text-primary" strokeWidth={1.75} />
                </div>
                <div>
                  <p className="font-medium text-[15px]">{title}</p>
                  <p className="text-sm text-muted-foreground">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          <FlowGraphic />
        </div>

        <div className="rounded-lg bg-secondary/60 px-4 py-3 flex items-start gap-2.5 max-w-lg">
          <Lock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" strokeWidth={1.75} />
          <p className="text-[13px] text-muted-foreground leading-relaxed">
            "Tu contenido es tuyo. <span className="font-medium text-foreground">No lo compartimos, no lo usamos para entrenar modelos</span> y puedes eliminarlo cuando quieras."
          </p>
        </div>
      </div>

      {/* Panel de formulario */}
      <div className="min-h-screen lg:min-h-0 grid place-items-center px-4 py-10 lg:border-l lg:bg-stone-100/40 dark:lg:bg-stone-900/30">
        <div className="w-full max-w-sm lg:max-w-md lg:rounded-xl lg:border lg:bg-card lg:p-10 animate-fade-up">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary text-primary-foreground grid place-items-center font-serif text-2xl">
              A
            </div>
            <h2 className="mt-4 font-serif text-4xl tracking-tight">Aviando</h2>
            <p className="mt-2 text-muted-foreground">Compártelo y olvídate.</p>
          </div>

          <form onSubmit={enter} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="pl-9"
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                <Input
                  id="password"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 pr-9"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  aria-label={showPass ? "Ocultar contraseña" : "Mostrar contraseña"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPass ? <EyeOff className="h-4 w-4" strokeWidth={1.75} /> : <Eye className="h-4 w-4" strokeWidth={1.75} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-muted-foreground cursor-pointer">
                <Checkbox defaultChecked aria-label="Recuérdame" /> Recuérdame
              </label>
              <button type="button" className="text-primary hover:underline" onClick={(e) => e.preventDefault()}>
                ¿Has olvidado tu contraseña?
              </button>
            </div>

            <Button type="submit" className="w-full" data-testid="login-submit-button">
              Entrar <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            o
            <span className="h-px flex-1 bg-border" />
          </div>

          <Button variant="outline" className="w-full" onClick={enter} data-testid="demo-login-button">
            <FlaskConical className="h-4 w-4" strokeWidth={1.75} /> Entrar como demo
          </Button>

          <p className="mt-4 text-center text-sm text-muted-foreground">
            ¿No tienes cuenta?{" "}
            <button type="button" className="text-primary font-medium hover:underline" data-testid="signup-link">
              Crear cuenta
            </button>
          </p>

          <p className="mt-8 flex items-center justify-center gap-4 text-xs text-muted-foreground">
            <button type="button" className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors">
              <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.75} /> Política de privacidad
            </button>
            <button type="button" className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors">
              <FileText className="h-3.5 w-3.5" strokeWidth={1.75} /> Términos de uso
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
